import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
interface TimingCardProps {
  title: string;
  location: string;
  date: string;
  description: string;
  index: number;
  isActive?: boolean;
  showApplyButton?: boolean;
  showNowOpenPill?: boolean;
}
const TimingCard = ({
  title,
  location,
  date,
  description,
  index,
  isActive,
  showApplyButton,
  showNowOpenPill
}: TimingCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-in");
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    if (cardRef.current) {
      observer.observe(cardRef.current);
    }
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);
  const handleApplyClick = () => {
    const element = document.getElementById('participation-criteria');
    if (element) {
      // Use offsetTop to avoid forced reflow
      const offsetPosition = element.offsetTop;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };
  return <div ref={cardRef} className={cn("p-6 rounded-2xl opacity-0 transition-all duration-300 hover:translate-y-[-5px] relative h-full flex flex-col", isActive ? "" : "")} style={{
    animationDelay: `${0.1 * index}s`,
    backgroundColor: isActive ? 'rgba(242, 76, 39, 0.08)' : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(8px)',
    border: isActive ? '1px solid rgba(242, 76, 39, 0.2)' : 'none',
    boxShadow: isActive ? '0 6px 24px rgba(242, 76, 39, 0.15)' : '0 4px 20px rgba(0, 0, 0, 0.08)'
  }}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{
        backgroundColor: isActive ? 'rgba(242, 76, 39, 1)' : 'rgba(77, 77, 77, 1)'
      }}>
          {index + 1}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold" style={{
            color: isActive ? 'rgba(242, 76, 39, 1)' : 'rgba(77, 77, 77, 1)'
          }}>
              {title}
            </h3>
            {showNowOpenPill && <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" style={{
            backgroundColor: 'rgba(242, 76, 39, 0.1)',
            border: '1px solid rgba(242, 76, 39, 0.2)',
            color: 'rgba(242, 76, 39, 1)'
          }}>
                Now Open
              </div>}
          </div>
          
        </div>
      </div>
      <p className="text-lg font-medium mb-2" style={{
      color: 'rgba(57, 55, 55, 1)'
    }}>
        {date}
      </p>
      <p className="text-sm mb-4" style={{
      color: 'rgba(77, 77, 77, 1)'
    }}>
        {description}
      </p>
      
      {showApplyButton && <Button onClick={handleApplyClick} className="w-full rounded-full font-medium transition-all duration-300 mt-auto" style={{
      backgroundColor: 'rgba(242, 76, 39, 1)',
      color: 'white'
    }}>
          Apply Now
        </Button>}
    </div>;
};
const Timing = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const timingData = [{
    title: "US Track",
    location: "United States",
    date: "October 26, 2025",
    description: "Kickoff in the heart of Silicon Valley with access to top US investors and tech companies.",
    isActive: true,
    showApplyButton: true,
    showNowOpenPill: true
  }, {
    title: "Europe Track",
    location: "Europe",
    date: "February 2026",
    description: "European innovation hub focusing on regulatory compliance and European market entry."
  }, {
    title: "Asia Track",
    location: "Asia",
    date: "May 2026",
    description: "Asian market expansion with focus on scaling and manufacturing partnerships."
  }, {
    title: "Worldwide Final",
    location: "Global",
    date: "August 2026",
    description: "Global championship bringing together the best teams from all regions for the ultimate showdown."
  }];
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const elements = entry.target.querySelectorAll(".fade-in-element");
          elements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add("animate-fade-in");
            }, index * 100);
          });
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section className="py-12 sm:py-16 md:py-20 pb-0 relative" id="timing" ref={sectionRef} style={{
    backgroundColor: '#F2F3EE'
  }}>
      <div className="container px-4 sm:px-6 lg:px-8 mx-auto">
        <div className="text-center mb-12 sm:mb-18">
          <div className="inline-flex items-center justify-center px-4 py-2 rounded-full mx-auto mb-8 opacity-0 fade-in-element" style={{
          backgroundColor: 'rgba(242, 76, 39, 0.1)',
          border: '1px solid rgba(242, 76, 39, 0.2)',
          color: 'rgba(242, 76, 39, 1)'
        }}>
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-white mr-2 text-sm font-medium" style={{
            backgroundColor: 'rgba(242, 76, 39, 1)'
          }}>3</span>
            <span>Timeline</span>
          </div>
          <h2 className="section-title mb-4 pb-2 opacity-0 fade-in-element text-3xl sm:text-4xl md:text-5xl font-display font-bold leading-[1.3] whitespace-nowrap" style={{
          background: 'linear-gradient(135deg, rgba(242, 76, 39, 1) 0%, rgba(31, 31, 30, 1) 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          color: 'transparent'
        }}>
            <span className="md:hidden">
              <span style={{ color: 'rgba(242, 76, 39, 1)' }}>4</span> Rounds, <span style={{ color: 'rgba(242, 76, 39, 1)' }}>4</span> Regions
            </span>
            <span className="hidden md:inline">
              <span style={{ color: 'rgba(242, 76, 39, 1)' }}>4</span> Rounds, <span style={{ color: 'rgba(242, 76, 39, 1)' }}>4</span> Regions, <span style={{ color: 'rgba(242, 76, 39, 1)' }}>1</span> Mission
            </span>
          </h2>
          <p className="section-subtitle mx-auto opacity-0 fade-in-element" style={{
          color: 'rgba(77, 77, 77, 1)'
        }}>
            Join the global AI revolution across four strategic regions throughout 2025-2026.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 items-stretch">
          {timingData.map((timing, index) => <TimingCard key={timing.title} title={timing.title} location={timing.location} date={timing.date} description={timing.description} index={index} isActive={timing.isActive} showApplyButton={timing.showApplyButton} showNowOpenPill={timing.showNowOpenPill} />)}
        </div>
      </div>
    </section>;
};
export default Timing;